import {useMemo} from 'react';

import {assertNever} from 'util/Types';

import {RealtimeVoiceSessionControls, RealtimeVoiceSessionState} from './useRealtimeVoiceSession';

/** Product-level states consumed by the composer. Keep provider details out of the view. */
export enum VoiceUIState {
  IDLE = 'idle',
  CONNECTING = 'connecting',
  /** Connected and waiting for the assistant; the user may or may not currently be speaking. */
  LISTENING = 'listening',
  AGENT_WORKING = 'agentWorking',
  AGENT_SPEAKING = 'agentSpeaking',
}

export interface VoiceUIController {
  state: VoiceUIState;
  isUserSpeaking: boolean;
  isAgentSpeaking: boolean;
  /** Live microphone input used only to drive the listening-cloud visualization. */
  microphoneLevel: RealtimeVoiceSessionControls['microphoneLevel'];
  start: () => Promise<void>;
  stop: () => Promise<void>;
}

function getVoiceUIState(controls: RealtimeVoiceSessionControls): VoiceUIState {
  switch (controls.sessionState) {
    case RealtimeVoiceSessionState.NOT_CONNECTED:
      return VoiceUIState.IDLE;
    case RealtimeVoiceSessionState.CONNECTING:
      return VoiceUIState.CONNECTING;
    case RealtimeVoiceSessionState.CONNECTED:
      if (controls.isAssistantSpeaking) {
        return VoiceUIState.AGENT_SPEAKING;
      }
      return controls.isAssistantWorking ? VoiceUIState.AGENT_WORKING : VoiceUIState.LISTENING;
    default:
      return assertNever(controls.sessionState);
  }
}

/** Thin adapter from transport controls to the stable semantic surface used by Voice UI. */
export default function useVoiceUIController(controls: RealtimeVoiceSessionControls): VoiceUIController {
  const {startVoiceSession, stopVoiceSession} = controls;
  const state = getVoiceUIState(controls);
  return useMemo(
    () => ({
      state,
      isUserSpeaking: controls.isUserSpeaking,
      isAgentSpeaking: controls.isAssistantSpeaking,
      microphoneLevel: controls.microphoneLevel,
      start: startVoiceSession,
      stop: stopVoiceSession,
    }),
    [
      controls.isAssistantSpeaking,
      controls.isUserSpeaking,
      controls.microphoneLevel,
      startVoiceSession,
      state,
      stopVoiceSession,
    ]
  );
}
